import type {
  CreationContext,
  ReportTemplateSelectionDialog,
} from '@backoffice-frontend/report-io-lib'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import {
  type ComponentProps,
  type ReactNode,
  createContext,
  useState,
} from 'react'
import { useAdOperatorReportIoLazyQuery } from './AdOperatorReportIo.hook'
import { getTripIdsFromWaypoints } from './getTripIdsFromWaypoints'

type ReportParams = Pick<
  ComponentProps<typeof ReportTemplateSelectionDialog>,
  'vehicleUuid' | 'fleetId' | 'tripIds' | 'creationContext'
>

type OpenReportDialogParams = {
  vehicleId: string
  creationContext: CreationContext
}

const useAdOperatorReportIoContextValue = () => {
  const [queryVehicle] = useAdOperatorReportIoLazyQuery()
  const [reportParams, setReportParams] = useState<ReportParams | undefined>()
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
  const hasRightsToReportProblem = useUserHasAccess({
    requiredRights: [
      BoumRight.REPORT_ACCIDENT_CRUD,
      BoumRight.REPORT_DISRUPTION_CRUD,
    ],
  })

  const openReportDialog = async ({
    vehicleId,
    creationContext,
  }: OpenReportDialogParams) => {
    if (!hasRightsToReportProblem) {
      return
    }

    const data = await queryVehicle({ variables: { vehicleId } })
    const vehicle = data.data?.vehicle

    if (!vehicle || !vehicle.fleet) {
      return
    }

    setReportParams({
      vehicleUuid: vehicle.id,
      fleetId: vehicle.fleet.id,
      tripIds: getTripIdsFromWaypoints(vehicle.fccUpcomingWaypoints),
      creationContext,
    })
    setIsReportDialogOpen(true)
  }

  const closeReportDialog = () => setIsReportDialogOpen(false)

  return {
    openReportDialog,
    closeReportDialog,
    isReportDialogOpen,
    reportParams,
  }
}

type AdOperatorReportIoContextValue = ReturnType<
  typeof useAdOperatorReportIoContextValue
>

export const AdOperatorReportIoContext = createContext<
  AdOperatorReportIoContextValue | undefined
>(undefined)

export const AdOperatorReportIoProvider = ({
  children,
}: { children: ReactNode }) => {
  const adOperatorReportIoContextValue = useAdOperatorReportIoContextValue()

  return (
    <AdOperatorReportIoContext.Provider value={adOperatorReportIoContextValue}>
      {children}
    </AdOperatorReportIoContext.Provider>
  )
}
